import React, { useContext, useMemo } from 'react';
import { RouteProps, Navigate, useLocation } from 'react-router-dom';
import authService from 'services/authService';
import credentialsService from 'services/credentialsService';
import { Role } from 'models/User';
import AuthContext from 'providers/Auth/Auth.context';
import CurrentUserContext from 'providers/CurrentUser';

export type EnhancedRouteProps = {
  authorized?: boolean;
  onlyPublic?: boolean;
  authorizedRoles?: Array<Role>;
} & Omit<RouteProps, 'element'> & {
    element: React.FC | React.ReactNode;
  };

const EnhancedRoute: React.FC<EnhancedRouteProps> = (props) => {
  const {
    authorized = false,
    onlyPublic = false,
    element,
    authorizedRoles,
  } = props;
  const { isLoggedIn } = useContext(AuthContext);
  const { user } = useContext(CurrentUserContext);

  const location = useLocation();

  const finalRoute = useMemo(() => {
    if ((authorized || authorizedRoles) && !isLoggedIn) {
      return (
        <Navigate
          to={{ pathname: '/login' }}
          state={{ from: location?.pathname }}
          replace
        />
      );
    }

    if (onlyPublic && isLoggedIn) {
      return <Navigate to="/" />;
    }

    if (authorizedRoles) {
      const userAllowed = authService.checkRoleForUser(user, authorizedRoles);
      if (!userAllowed) credentialsService.removeAuthBody();
    }

    if (typeof element === 'function') {
      const Component = element;

      return <Component />;
    }
    return element as React.ReactElement;
  }, [
    authorized,
    authorizedRoles,
    isLoggedIn,
    onlyPublic,
    location,
    user,
    element,
  ]);

  return finalRoute;
};

export default EnhancedRoute;
